import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link, Routes, Route } from 'react-router-dom';
import BlogPost from '../components/BlogPost';
import { Helmet } from 'react-helmet-async';
import apiService from '../services/apiService';

const BlogCard = ({ image, title, description, slug, publishedDate, author }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow"
    >
      <img src={image} alt={title} className="w-full h-64 object-cover"/>
      <div className="p-6">
        <div className="flex items-center text-sm text-gray-500 mb-2">
          <span>{author}</span>
          <span className="mx-2">•</span>
          <span>{new Date(publishedDate).toLocaleDateString()}</span>
        </div>
        <h3 className="text-2xl font-semibold text-gray-800 mb-4">{title}</h3>
        <p className="text-gray-600 mb-4 line-clamp-3">{description}</p>
        <Link
          to={slug}
          className="inline-block px-6 py-2 bg-blue-800 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          Read More
        </Link>
      </div>
    </motion.div>
  );
};

const LoadingSpinner = () => (
  <div className="flex justify-center items-center py-12">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2C2C90]"></div>
  </div>
);

const ErrorMessage = ({ message }) => {
  const isConfigError = message.includes('Airtable not configured');
  
  return (
    <div className="text-center py-12">
      <div className="text-red-600 text-lg mb-4">
        {isConfigError ? 'Airtable Configuration Required' : 'Error loading blog posts'}
      </div>
      <p className="text-gray-600 mb-4">{message}</p>
      {isConfigError && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-2xl mx-auto">
          <h3 className="font-semibold text-blue-800 mb-2">Setup Required:</h3>
          <ol className="text-left text-blue-700 space-y-1">
            <li>1. Create a <code className="bg-blue-100 px-1 rounded">.env</code> file in your project root</li>
            <li>2. Add your Airtable API key and Base ID</li>
            <li>3. Set up your Airtable base with "Blog Posts" table</li>
            <li>4. Restart the development server</li>
          </ol>
          <p className="text-sm text-blue-600 mt-3">
            See <code className="bg-blue-100 px-1 rounded">SETUP_INSTRUCTIONS.md</code> for detailed guide.
          </p>
        </div>
      )}
    </div>
  );
};

const BlogList = () => {
  const [blogs, setBlogs] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchBlogs = async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getPublicBlogs(page, pagination.limit);
      // Transform API response to shape used by BlogCard
      const transformed = (response.blogs || []).map(b => ({
        id: b.id,
        title: b.title,
        description: b.excerpt,
        slug: `/blog/${b.id}`,
        image: b.image,
        author: b.author,
        publishedDate: b.published_at
      }));
      setBlogs(transformed);
      setPagination(prev => ({ ...prev, total: response.total, page: response.page || page }));
    } catch (e) {
      setError(e.message || 'Failed to load blogs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogs(pagination.page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page]);

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;

  const totalPages = Math.max(1, Math.ceil((pagination.total || 0) / pagination.limit));

  return (
    <div>
      <div className="container mx-auto px-4 py-8">
        {/* Hero and Welcome Section */}
        <div className="flex flex-col md:flex-row gap-8 mb-12 md:mb-16">
          {/* Hero Section */}
          <div 
            className="relative w-full md:w-1/2 h-[250px] md:h-[300px] rounded-lg"
            style={{
              backgroundImage: 'url("/images/blog/plant-room.svg")',
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          >
            <div className="absolute inset-0 bg-[#2C2C90]/80 rounded-lg"></div>
            <div className="absolute inset-0 flex flex-col items-center justify-center text-white p-4">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-center">Blogs</h1>
            </div>
          </div>

          {/* Welcome Section */}
          <div className="w-full md:w-1/2 flex flex-col justify-center">
            <h2 className="text-3xl md:text-4xl font-bold text-[#2C2C90] mb-4 md:mb-6">
              Welcome to the Medical Gas Pipeline Blog
            </h2>
            <p className="text-gray-700 text-base md:text-lg">
              Dedicated to professionals in the medical Gas pipeline manufacturing industry and our blog provides comprehensive insights into pipeline systems. We explore the best practices in manufacturing, cutting-edge technological advancements, and important regulatory updates. Stay informed and ahead with our expert analysis and industry news.
            </p>
          </div>
        </div>

        {/* Blog List */}
        <div className="mb-12 md:mb-16">
          <h2 className="text-2xl md:text-3xl font-bold text-[#2C2C90] mb-6 md:mb-8">Latest Articles</h2>
          {blogs.length === 0 ? (
            <div className="text-center text-gray-600">No blog posts available.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {blogs.map(article => (
                <BlogCard key={article.id} {...article} />
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2">
            <button
              onClick={() => setPagination(p => ({ ...p, page: Math.max(1, p.page - 1) }))}
              disabled={pagination.page === 1}
              className={`px-3 py-1 border rounded ${pagination.page === 1 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100'}`}
            >
              Prev
            </button>
            <span className="text-sm text-gray-600">Page {pagination.page} of {totalPages}</span>
            <button
              onClick={() => setPagination(p => ({ ...p, page: Math.min(totalPages, p.page + 1) }))}
              disabled={pagination.page === totalPages}
              className={`px-3 py-1 border rounded ${pagination.page === totalPages ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100'}`}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const Blog = () => {
  return (
    <>
      <Helmet>
        <title>Hospital Build Blog | Medical Gas Pipeline News & Articles</title>
        <meta name="description" content="Read the latest news, articles, and updates from Hospital Build Pvt Ltd on medical gas pipeline systems, hospital equipment, and healthcare infrastructure." />
        <meta name="keywords" content="Hospital Build Blog, Medical Gas Pipeline News, Hospital Equipment Articles, Healthcare Infrastructure Blog, MGPS Updates" />
        <link rel="canonical" href="https://www.hospitalbuild.in/blog" />
      </Helmet>
      <Routes>
        <Route path="/" element={<BlogList />} />
        <Route path=":id" element={<BlogPost />} />
      </Routes>
    </>
  );
};

export { Blog }; 
